const EMPTY_SNAPSHOT = Object.freeze({
    domains: Object.freeze([]),
});
/** Origin-qualified, transport-free state machine for trusted client controls. */
export class SecureClientBindingStoreV1 {
    domains = new Map();
    currentSnapshot = EMPTY_SNAPSHOT;
    apply(control) {
        const key = domainKey(control.domain);
        const current = this.domains.get(key);
        const draft = current ? cloneDomain(current) : emptyDomain(control.domain);
        const result = applyControl(draft, control);
        if (result.kind !== "applied")
            return result;
        this.domains.set(key, draft);
        this.currentSnapshot = snapshotOf(this.domains);
        return result;
    }
    snapshot() {
        return this.currentSnapshot;
    }
    binding(domain, clientId) {
        return (this.domains.get(domainKey(domain))?.bindings.get(clientId) ??
            Object.freeze({ state: "unbound" }));
    }
    clear() {
        if (this.domains.size === 0)
            return false;
        this.domains.clear();
        this.currentSnapshot = EMPTY_SNAPSHOT;
        return true;
    }
}
function applyControl(state, control) {
    switch (control.type) {
        case "binding_snapshot":
            if (control.binding.state === "unbound") {
                if (state.bindings.size === 0 &&
                    state.presence.size === 0 &&
                    state.highWater.size === 0) {
                    return ignored("idempotent");
                }
                state.bindings.clear();
                state.presence.clear();
                state.highWater.clear();
                return applied();
            }
            return control.binding.state === "bound"
                ? applyBinding(state, control.binding)
                : applyTombstone(state, control.binding.clientId, control.binding.bindingGeneration);
        case "binding_upsert":
            return applyBinding(state, control.binding);
        case "binding_tombstone":
            return applyTombstone(state, control.clientId, control.bindingGeneration);
        case "presence_snapshot": {
            const seen = new Set();
            let changed = false;
            let stale = false;
            for (const entry of control.entries) {
                if (seen.has(entry.clientId)) {
                    return terminal("binding_protocol_violation");
                }
                seen.add(entry.clientId);
                const result = applyPresence(state, entry, control.version);
                if (result.kind === "terminal")
                    return result;
                if (result.kind === "applied")
                    changed = true;
                if (result.kind === "ignored" && result.reason === "stale") {
                    stale = true;
                }
            }
            return changed ? applied() : ignored(stale ? "stale" : "idempotent");
        }
        case "presence_upsert":
            return applyPresence(state, control.entry, control.version);
        case "presence_remove": {
            const binding = state.bindings.get(control.clientId);
            if (!binding)
                return ignored("stale");
            const comparison = compareGeneration(control.bindingGeneration, binding.bindingGeneration);
            if (comparison < 0)
                return ignored("stale");
            if (comparison > 0) {
                return terminal("binding_protocol_violation");
            }
            if (binding.state === "tombstoned")
                return ignored("idempotent");
            return state.presence.delete(control.clientId)
                ? applied()
                : ignored("idempotent");
        }
    }
    return terminal("binding_protocol_violation");
}
function applyBinding(state, incoming) {
    const existing = state.bindings.get(incoming.clientId);
    if (!existing) {
        state.bindings.set(incoming.clientId, freezeBinding(incoming));
        clearPresenceDomain(state, incoming.clientId);
        return applied();
    }
    const comparison = compareGeneration(incoming.bindingGeneration, existing.bindingGeneration);
    if (existing.state === "tombstoned") {
        if (comparison <= 0) {
            return ignored(comparison === 0 ? "idempotent" : "stale");
        }
        state.bindings.set(incoming.clientId, freezeBinding(incoming));
        clearPresenceDomain(state, incoming.clientId);
        return applied();
    }
    if (comparison < 0)
        return ignored("stale");
    if (comparison > 0)
        return terminal("binding_collision");
    if (!sameBindingIdentity(existing, incoming)) {
        return terminal("binding_collision");
    }
    const authorityComparison = compareGeneration(incoming.resourceAuthorizationGeneration, existing.resourceAuthorizationGeneration);
    if (authorityComparison < 0)
        return ignored("stale");
    if (authorityComparison === 0) {
        return equalBinding(existing, incoming)
            ? ignored("idempotent")
            : terminal("binding_protocol_violation");
    }
    if (incoming.lastAwarenessClock !== existing.lastAwarenessClock) {
        return terminal("binding_protocol_violation");
    }
    state.bindings.set(incoming.clientId, freezeBinding(incoming));
    return applied();
}
function applyTombstone(state, clientId, bindingGeneration) {
    const existing = state.bindings.get(clientId);
    if (!existing) {
        state.bindings.set(clientId, Object.freeze({ state: "tombstoned", clientId, bindingGeneration }));
        clearPresenceDomain(state, clientId);
        return applied();
    }
    const comparison = compareGeneration(bindingGeneration, existing.bindingGeneration);
    if (comparison < 0)
        return ignored("stale");
    if (comparison > 0)
        return terminal("binding_protocol_violation");
    if (existing.state === "tombstoned")
        return ignored("idempotent");
    state.bindings.set(clientId, Object.freeze({ state: "tombstoned", clientId, bindingGeneration }));
    clearPresenceDomain(state, clientId);
    return applied();
}
function applyPresence(state, entry, version) {
    const binding = state.bindings.get(entry.clientId);
    if (!binding ||
        binding.state !== "bound" ||
        binding.bindingGeneration !== entry.bindingGeneration ||
        !validClock(entry.clock)) {
        return terminal("binding_protocol_violation");
    }
    const prior = state.highWater.get(entry.clientId);
    if (prior) {
        if (entry.bindingGeneration !== prior.bindingGeneration) {
            return terminal("binding_protocol_violation");
        }
        if (entry.clock < prior.clock)
            return ignored("stale");
        if (entry.clock === prior.clock) {
            // A V2 snapshot sent immediately after negotiation may enrich the V1
            // snapshot at the same awareness clock. Only the presenter field may differ.
            if (version === 2 &&
                prior.version === 1 &&
                equalPresence(stripPresenterViewport(entry.value), prior.value)) {
                if (entry.value === null)
                    state.presence.delete(entry.clientId);
                else
                    state.presence.set(entry.clientId, entry);
                state.highWater.set(entry.clientId, Object.freeze({
                    bindingGeneration: entry.bindingGeneration,
                    clock: entry.clock,
                    value: entry.value,
                    version,
                }));
                return applied();
            }
            return equalPresence(entry.value, prior.value)
                ? ignored("idempotent")
                : terminal("awareness_protocol_violation");
        }
    }
    if (entry.value === null)
        state.presence.delete(entry.clientId);
    else
        state.presence.set(entry.clientId, entry);
    state.highWater.set(entry.clientId, Object.freeze({
        bindingGeneration: entry.bindingGeneration,
        clock: entry.clock,
        value: entry.value,
        version,
    }));
    state.bindings.set(entry.clientId, freezeBinding({ ...binding, lastAwarenessClock: entry.clock }));
    return applied();
}
function stripPresenterViewport(value) {
    if (value === null)
        return null;
    const { presenterViewport: _presenterViewport, ...rest } = value;
    return rest;
}
function clearPresenceDomain(state, clientId) {
    state.presence.delete(clientId);
    state.highWater.delete(clientId);
}
function snapshotOf(domains) {
    return Object.freeze({
        domains: Object.freeze([...domains.values()]
            .sort((left, right) => domainKey(left.domain).localeCompare(domainKey(right.domain)))
            .map((state) => Object.freeze({
            domain: state.domain,
            bindings: Object.freeze([...state.bindings.values()].sort(byBindingClientId)),
            presence: Object.freeze([...state.presence.values()].sort(byPresenceClientId)),
        }))),
    });
}
function emptyDomain(domain) {
    return {
        domain,
        bindings: new Map(),
        presence: new Map(),
        highWater: new Map(),
    };
}
function cloneDomain(state) {
    return {
        domain: state.domain,
        bindings: new Map(state.bindings),
        presence: new Map(state.presence),
        highWater: new Map(state.highWater),
    };
}
function domainKey(domain) {
    return JSON.stringify([
        domain.originId,
        domain.document.tenantId,
        domain.document.docType,
        domain.document.entityId,
        domain.documentGeneration,
    ]);
}
function compareGeneration(left, right) {
    const a = BigInt(left);
    const b = BigInt(right);
    return a < b ? -1 : a > b ? 1 : 0;
}
function sameBindingIdentity(left, right) {
    return (left.connectionKey === right.connectionKey &&
        left.connectionGeneration === right.connectionGeneration &&
        left.clientId === right.clientId &&
        left.bindingGeneration === right.bindingGeneration &&
        left.userId === right.userId);
}
function equalBinding(left, right) {
    return JSON.stringify(left) === JSON.stringify(right);
}
function equalPresence(left, right) {
    return JSON.stringify(left) === JSON.stringify(right);
}
function freezeBinding(binding) {
    return Object.freeze({ ...binding });
}
function validClock(clock) {
    return Number.isSafeInteger(clock) && clock >= 0;
}
function byBindingClientId(left, right) {
    return left.clientId - right.clientId;
}
function byPresenceClientId(left, right) {
    return left.clientId - right.clientId;
}
function applied() {
    return Object.freeze({ kind: "applied" });
}
function ignored(reason) {
    return Object.freeze({ kind: "ignored", reason });
}
function terminal(reason) {
    return Object.freeze({ kind: "terminal", reason });
}
//# sourceMappingURL=binding-store.js.map